import { api } from './client'
import { getLanguage } from '../i18n'

// index_drop_pct is optional: a row the user started but has not filled in is
// what the server reports back in `missing`.
export type StressScenario = { name: string; index_drop_pct?: number }

// §3.4. Every field is optional because the ledger has no defaults: a value is
// present only once the user wrote it down, and anything absent comes back in
// `missing`.
export type LedgerSettings = {
  account?: {
    loss_budget_pct?: number
    exposure_cap_e?: number
    single_name_cap_pct?: number
    sector_cap_pct?: number
    drawdown_warn_pct?: number
    drawdown_pause_pct?: number
    equity_high_water?: number
  }
  margin?: {
    enabled?: boolean
    l_cap?: number
    cost_pct?: number
    maintenance_buffer_pct?: number
    profit_trigger_pct?: number
    post_profit_l_target?: number
    post_profit_loss_budget_pct?: number
  }
  stress?: { scenarios?: StressScenario[] }
  framework?: {
    inflation?: { metric?: string; pass_max?: number; fail_above?: number; consecutive?: number }
    energy?: { metric?: string; pass_max?: number; warn_above?: number; fail_above?: number }
    policy?: { target_meeting?: string; pass_max_net_hikes?: number; fail_above_net_hikes?: number; reading_ttl_days?: number }
    rates?: { warn_5d_bp?: number; fail_no_retrace_days?: number; retrace_target_pct?: number }
    earnings?: { universe?: string[] }
  }
}

export type LedgerSettingsResponse = {
  enabled: boolean
  version: number
  settings: LedgerSettings
  complete: boolean
  missing: string[]
}

export type LedgerTrigger = { key: string; breached: boolean; why: string }
export type LedgerStress = { name: string; loss: number; budget?: number | null; breach: boolean }
export type LedgerExposure = { symbol: string; market_value: number; beta: number; beta_assumed: boolean }

export type LedgerAccountCard = {
  n?: number | null
  e?: number | null
  l?: number | null
  exposure_by_bucket?: { core?: number | null; event?: number | null }
  stress: LedgerStress[]
  drawdown_pct?: number | null
  margin_call_drop_pct?: number | null
  triggers: LedgerTrigger[]
  holdings?: LedgerExposure[]
  partial?: string[]
  // C11: premium already paid for open long calls, and how many contracts.
  // Deliberately outside E, L, stress and triggers — it is a sunk maximum loss.
  long_call_premium: number
  long_call_count: number
}

export type LedgerConditionState = 'pass' | 'fail' | 'unknown'

export type LedgerCondition = {
  key: string
  state: LedgerConditionState
  value?: number | string | null
  why: string
  needs_input?: boolean
  // The conditions that, on their own, put the framework into scenario C.
  invalidated?: boolean
}

export type LedgerFrameworkCard = {
  scenario: 'A' | 'B' | 'C'
  label: string
  conditions: LedgerCondition[]
  downgrade_at?: string
  upgrade_requires?: string
}

export type LedgerStatus = {
  as_of?: string
  account_card: LedgerAccountCard
  framework_card: LedgerFrameworkCard
}

export type FedWatchOutcome = { midpoint: number; prob: number }
export type FedWatchPayload = { meeting: string; m0: number; outcomes: FedWatchOutcome[]; view?: string }

// change_vs_prev is the delta against the previous reading for the SAME meeting;
// it is absent, or carries comparable: false, when there is nothing to compare.
export type FedWatchChange = { comparable: boolean; mu_t: number; delta_r: number; net_hikes: number }

export type FedWatchResult = {
  mu_t: number
  delta_r: number
  net_hikes: number
  change_vs_prev?: FedWatchChange | null
}

export type LedgerReviewAnswers = {
  scenario_judgement?: string
  rate_shock?: string
  profit_mgmt?: string
  account_risk?: string
  exec_cost?: string
  vs_baseline?: string
}

export type LedgerReview = {
  answers: LedgerReviewAnswers
  judged_correct: boolean | null
}

export function fetchLedgerSettings(): Promise<LedgerSettingsResponse> {
  return api<LedgerSettingsResponse>('/v1/ledger/settings')
}

export function saveLedgerSettings(body: { enabled?: boolean; settings?: LedgerSettings }): Promise<LedgerSettingsResponse> {
  return api<LedgerSettingsResponse>('/v1/ledger/settings', { method: 'PUT', body: JSON.stringify(body) })
}

// Server-side labels and `why` strings follow ?lang=, same as /v1/macro/cycle.
export function fetchLedgerStatus(): Promise<LedgerStatus> {
  return api<LedgerStatus>(`/v1/ledger/status?lang=${getLanguage()}`)
}

export function postFedWatchReading(payload: FedWatchPayload, validUntil: string): Promise<FedWatchResult> {
  return api<FedWatchResult>('/v1/ledger/readings', {
    method: 'POST',
    body: JSON.stringify({ key: 'fedwatch', payload, valid_until: validUntil }),
  })
}

export function fetchLedgerReview(eventId: string): Promise<LedgerReview> {
  return api<LedgerReview>(`/v1/ledger/reviews/${encodeURIComponent(eventId)}`)
}

export function saveLedgerReview(eventId: string, body: LedgerReview): Promise<LedgerReview> {
  return api<LedgerReview>(`/v1/ledger/reviews/${encodeURIComponent(eventId)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}
